package com.evoapps.evoshift.domain.usecase.tips

import com.evoapps.evoshift.domain.model.ClockReviewSettings
import com.evoapps.evoshift.domain.model.BreakPolicy
import com.evoapps.evoshift.domain.model.RosterIds
import com.evoapps.evoshift.domain.model.TipPeriod
import com.evoapps.evoshift.domain.model.TipPool
import com.evoapps.evoshift.domain.model.TipPoolDraft
import com.evoapps.evoshift.domain.model.TipShare
import com.evoapps.evoshift.domain.model.TipSplit
import com.evoapps.evoshift.domain.repository.ShiftRepository
import com.evoapps.evoshift.domain.repository.TipRepository
import com.evoapps.evoshift.domain.repository.UserRepository
import com.evoapps.evoshift.domain.repository.WorkplaceRepository
import kotlinx.coroutines.async
import kotlinx.coroutines.awaitAll
import kotlinx.coroutines.coroutineScope
import kotlinx.datetime.LocalDate

/** SH-124: what a pool would give each person, before it is saved. */
data class TipPreview(
    val period: TipPeriod,
    val startDate: String,
    val endDate: String,
    val currency: String,
    /** The roles whose hours count in the period (for the weights), sorted. */
    val roles: List<String>,
    val shares: List<TipShare>
)

/**
 * SH-124: a manager's preview of a pool of [amountMinor] for the [period] holding [date]: the
 * workplace's clocked shifts of the period (by roster month), its break policy, the people's
 * names and roles, and [TipSplit.shares]. The server repeats the same split when saving.
 */
class PreviewTipSplitUseCase(
    private val shiftRepository: ShiftRepository,
    private val userRepository: UserRepository,
    private val workplaceRepository: WorkplaceRepository
) {
    suspend operator fun invoke(
        workplaceId: String,
        period: TipPeriod,
        date: LocalDate,
        amountMinor: Long,
        roleWeights: Map<String, Int>
    ): Result<TipPreview> = runCatching {
        coroutineScope {
            val workplace = async { workplaceRepository.getWorkplace(workplaceId).getOrNull() }
            val team = async { userRepository.getAssignableEmployees(workplaceId).getOrDefault(emptyList()) }
            val firstDay = workplace.await()?.settings?.firstDayOfWeek ?: 1
            val (start, end) = TipSplit.periodOf(period, date, firstDay)
            val from = start.toString()
            val to = end.toString()
            val shifts = RosterIds.monthsBetween(from, to)
                .map { (y, m) -> async { shiftRepository.getShiftsForRoster(RosterIds.of(workplaceId, y, m)).getOrThrow() } }
                .awaitAll().flatten()
                .filter { it.date in from..to }
            val people = team.await().associateBy { it.uid }.toMutableMap()
            shifts.mapNotNull { it.actualUserId ?: it.userId }.distinct().filter { it !in people }.forEach { uid ->
                userRepository.getUserProfile(uid).getOrNull()?.let { people[uid] = it }
            }
            val policy = workplace.await()?.settings?.breakPolicy ?: BreakPolicy()
            val shares = TipSplit.shares(
                amountMinor, shifts, workplaceId, from, to, policy, roleWeights, people,
                workplace.await()?.settings?.clockReview ?: ClockReviewSettings()
            )
            val roles = shifts.filter { it.userId != null && it.actualTime?.end?.isNotEmpty() == true }
                .mapNotNull { s -> s.requiredRole?.takeIf { it.isNotBlank() } ?: people[s.userId]?.engagements?.get(workplaceId)?.role }
                .distinct().sorted()
            TipPreview(period, from, to, workplace.await()?.settings?.currency ?: "PLN", roles, shares)
        }
    }
}

/** SH-124: checks a manager's pool ([TipSplit.validate]) and saves it; the server splits it. */
class SaveTipPoolUseCase(private val tipRepository: TipRepository) {
    suspend operator fun invoke(draft: TipPoolDraft): Result<TipPool> = runCatching {
        TipSplit.validate(draft)
        // Only the weights that change something travel.
        val weights = draft.roleWeights.filterValues { it != TipSplit.DEFAULT_WEIGHT }
        tipRepository.savePool(draft.copy(roleWeights = weights)).getOrThrow()
    }
}
